/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { resetAppState } from './resetAppState';
import { SESSION_KEYS } from '../lib/authConstants';
import { emitGlobalNotification } from './globalNotification';
import { clearEntryTarget, isEmailDeepLinkPath } from './loginRedirect';

const AUTHORIZATION_SERVICE_UNAVAILABLE_MESSAGE = 'Authorization service temporarily unavailable, try again after sometime';

const debugAuth = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.info(...args);
  }
};

class AuthManager {
  constructor() {
    this.TIMEOUT_MINUTES = 10;
    this.TIMEOUT_MS = this.TIMEOUT_MINUTES * 60 * 1000;
    this.activityTimer = null;
    this.refreshTimer = null;
    this.validationTimer = null;
    this.storageListener = null;
    this.isInitialized = false;
    this.isCheckingSession = false;
    this.isLoggingOut = false;
    this.lastSessionCheck = 0;
    this.postLoginCallbacks = [];
  }

  notifyAuthStateChanged() {
    window.dispatchEvent(new Event('auth-state-changed'));
  }

  init() {
    if (this.isInitialized) return;
    this.isInitialized = true;
    this.setupActivityTracking();
    this.setupStorageListener();
    this.checkExistingSession();
    
    window.updateAuthState = () => {
      this.notifyAuthStateChanged();
    };
  }

  setupActivityTracking() {
    const activities = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const resetActivity = () => this.resetActivityTimer();

    activities.forEach(activity => {
      document.addEventListener(activity, resetActivity, true);
    });

    if (this.isAuthenticated()) {
      this.resetActivityTimer();
      this.startRefreshTimer();
      this.startValidationTimer();
    }
  }

  setupStorageListener() {
    // Storage events are sufficient here because auth state is already mirrored in localStorage,
    // and this keeps cross-tab sync simpler and more widely supported than BroadcastChannel.
    this.storageListener = (e) => {
      if (e.key === SESSION_KEYS.IS_AUTHENTICATED) {
        if (e.newValue === 'true') {
          const hasSessionAuth = sessionStorage.getItem(SESSION_KEYS.IS_AUTHENTICATED) === 'true';

          if (!hasSessionAuth) {
            debugAuth('AuthManager: storage: auth=true detected and this tab not authenticated, restoring');
            this.restoreSessionFromLocalStorage();
            return;
          }
        } else {
          debugAuth('AuthManager: storage: auth cleared in another tab, syncing logout locally');
          void this.syncLogoutFromStorage();
          return;
        }
        
        if (window.updateAuthState) {
          debugAuth('AuthManager: storage: calling updateAuthState');
          window.updateAuthState();
        }
      } else if (e.key === SESSION_KEYS.LAST_ACTIVITY && e.newValue) {
        this.resetActivityTimer();
      }
    };
    window.addEventListener('storage', this.storageListener);
  }

  resetActivityTimer() {
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
    }

    if (!this.isAuthenticated()) {
      return;
    }

    const now = Date.now();
    sessionStorage.setItem(SESSION_KEYS.LAST_ACTIVITY, now.toString());
    localStorage.setItem(SESSION_KEYS.LAST_ACTIVITY, now.toString());

    this.activityTimer = setTimeout(() => {
      debugAuth('AuthManager: activityTimer fired (inactivity timeout reached)');
      this.handleInactivityTimeout();
    }, this.TIMEOUT_MS);
  }

  handleInactivityTimeout() {
    if (this.isAuthenticated() && !this.isLoggingOut) {
      debugAuth('AuthManager: inactivity timeout reached, triggering logout');

      this.logout();
    }
  }

  async checkExistingSession() {
    if (this.isCheckingSession || this.isLoggingOut) return;
    
    const now = Date.now();
    if (now - this.lastSessionCheck < 1000) return;
    
    this.isCheckingSession = true;
    this.lastSessionCheck = now;

    try {
      const sessionAuth = sessionStorage.getItem(SESSION_KEYS.IS_AUTHENTICATED);
      const localAuth = localStorage.getItem(SESSION_KEYS.IS_AUTHENTICATED);
      
      if (!sessionAuth && localAuth && !this.isLoggingOut) {
        debugAuth('AuthManager: checkExistingSession: found local auth without session, restoring sessionStorage from localStorage');
        this.restoreSessionFromLocalStorage();
        return;
      }
      
      if (!this.isAuthenticated() && !sessionAuth && !localAuth) {
        debugAuth('AuthManager: checkExistingSession: no authenticated session present');
        return;
      }
      
      if (this.isAuthenticated()) {
        const lastActivity = sessionStorage.getItem(SESSION_KEYS.LAST_ACTIVITY);
        if (lastActivity) {
          const timeSinceActivity = Date.now() - parseInt(lastActivity);
          if (timeSinceActivity > this.TIMEOUT_MS && !this.isLoggingOut) {
            this.logout();
            return;
          }
        }
        this.resetActivityTimer();
        this.startRefreshTimer();
        this.startValidationTimer();
      }
      
    } catch (error) {
      console.error('Session check failed:', error);
    } finally {
      this.isCheckingSession = false;
      debugAuth('AuthManager: checkExistingSession: finished');
    }
  }

  restoreSessionFromLocalStorage() {
    const localAuth = localStorage.getItem(SESSION_KEYS.IS_AUTHENTICATED);
    const localToken = localStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
    const localData = localStorage.getItem(SESSION_KEYS.USER_DATA);
    const localStart = localStorage.getItem(SESSION_KEYS.SESSION_START);
    
    if (localAuth === 'true') {
      sessionStorage.setItem(SESSION_KEYS.IS_AUTHENTICATED, localAuth);
      if (localToken) sessionStorage.setItem(SESSION_KEYS.AUTH_TOKEN, localToken);
      if (localData) sessionStorage.setItem(SESSION_KEYS.USER_DATA, localData);
      if (localStart) sessionStorage.setItem(SESSION_KEYS.SESSION_START, localStart);
      
      this.resetActivityTimer();
      this.startRefreshTimer();
      this.startValidationTimer();
      this.triggerSessionRestoration();
      
      debugAuth('AuthManager: session restored from localStorage');

      if (window.updateAuthState) {
        window.updateAuthState();
      }
    }
  }

  async syncLogoutFromStorage() {
    // A sign-out in another tab retires this tab's entry URL too.
    clearEntryTarget();

    sessionStorage.removeItem(SESSION_KEYS.IS_AUTHENTICATED);
    sessionStorage.removeItem(SESSION_KEYS.AUTH_TOKEN);
    sessionStorage.removeItem(SESSION_KEYS.USER_DATA);
    sessionStorage.removeItem(SESSION_KEYS.LAST_ACTIVITY);
    sessionStorage.removeItem(SESSION_KEYS.SESSION_START);

    localStorage.removeItem(SESSION_KEYS.IS_AUTHENTICATED);
    localStorage.removeItem(SESSION_KEYS.AUTH_TOKEN);
    localStorage.removeItem(SESSION_KEYS.USER_DATA);
    localStorage.removeItem(SESSION_KEYS.LAST_ACTIVITY);
    localStorage.removeItem(SESSION_KEYS.SESSION_START);

    await this.clearLocalAuth();

    window.dispatchEvent(
      new CustomEvent('session-expired', {
        detail: { message: 'Your session has expired due to inactivity, login again to continue' }
      })
    );

    if (window.updateAuthState) {
      window.updateAuthState();
    } else {
      this.notifyAuthStateChanged();
    }
  }

  triggerSessionRestoration() {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('session-restored'));
    }, 100);
  }

  login(userData = null, options = {}) {
    const { authToken = null } = options;
    const now = Date.now().toString();

    this.isLoggingOut = false;

    if (window.__REDUX_STORE__) {
      window.__REDUX_STORE__.dispatch({ type: 'admin/clearAdmin' });
    }
    
    sessionStorage.setItem(SESSION_KEYS.IS_AUTHENTICATED, 'true');
    sessionStorage.setItem(SESSION_KEYS.SESSION_START, now);
    localStorage.setItem(SESSION_KEYS.IS_AUTHENTICATED, 'true');
    localStorage.setItem(SESSION_KEYS.SESSION_START, now);

    if (authToken) {
      sessionStorage.setItem(SESSION_KEYS.AUTH_TOKEN, authToken);
      localStorage.setItem(SESSION_KEYS.AUTH_TOKEN, authToken);
    } else {
      sessionStorage.removeItem(SESSION_KEYS.AUTH_TOKEN);
      localStorage.removeItem(SESSION_KEYS.AUTH_TOKEN);
    }
    
    if (userData) {
      const userDataStr = JSON.stringify(userData);
      sessionStorage.setItem(SESSION_KEYS.USER_DATA, userDataStr);
      localStorage.setItem(SESSION_KEYS.USER_DATA, JSON.stringify({
        id: userData.id,
        username: userData.username,
        email: userData.email,
        password_changed: userData.password_changed
      }));
    }

    debugAuth('AuthManager: login - session stored');
    
    this.resetActivityTimer();
    this.startRefreshTimer();
    this.startValidationTimer();
    this.notifyAuthStateChanged();
  }

  startRefreshTimer() {
    if (!this.isAuthenticated()) return;

    if (this.getAuthToken()) {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
      return;
    }

    const refreshInterval = 4 * 60 * 1000; // 4 minutes
    
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }

    debugAuth('AuthManager: startRefreshTimer');

    this.refreshTimer = setInterval(async () => {
      if (!this.isAuthenticated() || this.isLoggingOut) {
        debugAuth('AuthManager: refresh aborted (not authenticated or logging out)');
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
        return;
      }
      
      if (window.location.pathname === '/' || window.location.pathname === '/login') {
        return;
      }

      try {
        await this.attemptRefresh();
      } catch (error) {
        console.error('AuthManager: attemptRefresh threw', { error: (error && error.message) || error });
      }
    }, refreshInterval);
  }
  
  async attemptRefresh() {
    try {
      const { refreshSession } = await import('../api/authService');
      const refreshed = await refreshSession();
      
      if (refreshed) {
        debugAuth('AuthManager: attemptRefresh: session refreshed successfully');
        return true;
      }

      if (!this.isLoggingOut && this.isAuthenticated()) {
        debugAuth('AuthManager: attemptRefresh: refresh failed, logging out');
        this.logout();
      }
      return false;
    } catch (error) {
      debugAuth('AuthManager: attemptRefresh: error during refresh', { error: (error && error.message) || error });
      if (!this.isLoggingOut && this.isAuthenticated()) {
        this.logout();
      }
      throw error;
    }
  }

  startValidationTimer() {
    if (!this.isAuthenticated()) return;
    
    const validationInterval = 5 * 60 * 1000; // Check every 5 minutes
    
    if (this.validationTimer) {
      clearInterval(this.validationTimer);
    }
    
    this.validationTimer = setInterval(async () => {
      if (!this.isAuthenticated() || this.isLoggingOut) {
        clearInterval(this.validationTimer);
        this.validationTimer = null;
        return;
      }
      
      if (window.location.pathname === '/' || window.location.pathname === '/login') {
        return;
      }
      
      try {
        const { checkCurrentUser } = await import('../api/authService');
        await checkCurrentUser();
      } catch (error) {
        if (error.response?.status === 401 && !this.isLoggingOut && this.isAuthenticated()) {
          this.logout();
        }
      }
    }, validationInterval);
  }

  handleAuthorizationServiceUnavailable(requestId) {
    if (this.isLoggingOut) {
      return;
    }

    emitGlobalNotification({
      message: AUTHORIZATION_SERVICE_UNAVAILABLE_MESSAGE,
      severity: 'error',
      referenceId: requestId || undefined,
    });

    if (this.isAuthenticated()) {
      this.logout(false, { suppressSessionExpiredEvent: true });
    }
  }

  logout(isManual = false, options = {}) {
    const { suppressSessionExpiredEvent = false } = options;

    if (this.isLoggingOut || !this.isAuthenticated()) {
      return Promise.resolve(false);
    }

    debugAuth('AuthManager: logout triggered', { manual: !!isManual });

    // Cleared before anything re-renders, so the route guard cannot carry the
    // page being logged out of through to the login screen.
    clearEntryTarget();
    this.isLoggingOut = true;

    return this.clearLocalAuth()
      .then(() => {
        this.callLogoutAPI();

        return new Promise((resolve) => {
          setTimeout(() => {
            if (!isManual && !suppressSessionExpiredEvent) {
              window.dispatchEvent(
                new CustomEvent("session-expired", {
                  detail: { message: "Your session has expired due to inactivity, login again to continue" }
                })
              );
            }
            this.redirectToLogin();
            resolve(true);
          }, 100);
        });
      })
      .catch((error) => {
        this.isLoggingOut = false;
        throw error;
      });
  }
  
  async callLogoutAPI() {
    try {
      const { logoutUser } = await import('../api/authService');
      
      debugAuth('AuthManager: calling logout API');
      
      await logoutUser();
    } catch (error) {
      console.error('Logout API call failed:', error);
    }
  }

  async clearLocalAuth() {
    await resetAppState()

    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
      this.activityTimer = null;
    }

    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.validationTimer) {
      clearInterval(this.validationTimer);
      this.validationTimer = null;
    }

    this.isCheckingSession = false;
    this.lastSessionCheck = 0;
  }
  redirectToLogin() {
    if (window.updateAuthState) {
      window.updateAuthState();
      this.isLoggingOut = false;
    } else {
      window.location.href = '/';
    }
  }

  isAuthenticated() {
    const sessionAuth = sessionStorage.getItem(SESSION_KEYS.IS_AUTHENTICATED) === 'true';
    const localAuth = localStorage.getItem(SESSION_KEYS.IS_AUTHENTICATED) === 'true';
    
    if (sessionAuth) {
      return true;
    }

    return localAuth;
  }

  getUserData() {
    const data = sessionStorage.getItem(SESSION_KEYS.USER_DATA) || 
                 localStorage.getItem(SESSION_KEYS.USER_DATA);
    return data ? JSON.parse(data) : null;
  }

  getAuthToken() {
    return sessionStorage.getItem(SESSION_KEYS.AUTH_TOKEN) ||
           localStorage.getItem(SESSION_KEYS.AUTH_TOKEN) ||
           null;
  }

  requiresPasswordReset() {
    return this.getUserData()?.password_changed === false;
  }


  // Only an email deep-link path is honored: `redirectTo` originates from a URL
  // query param, and forwarding it unchecked to `navigate` would let a crafted
  // link drop a freshly logged-in admin anywhere in the app.
  isSafeInternalRedirect(path) {
    if (typeof path !== 'string' || !path.startsWith('/admin/')) {
      return false;
    }

    if (path.startsWith('//') || path.includes('://')) {
      return false;
    }

    return isEmailDeepLinkPath(path.split('?')[0]);
  }

  async handlePostLogin(navigate, updateAbility, showNotification, options = {}) {
    const {
      prefetchedAdmin = null,
      successMessage = 'Logged in successfully',
      showSuccessToast = true,
      redirectTo = null,
    } = options;

    try {
      const admin = prefetchedAdmin || (await (async () => {
        const { checkCurrentUser } = await import('../api/authService');
        return checkCurrentUser();
      })());

      if (admin) {
        if (!this.isAuthenticated()) {
          this.login(admin);
        }
        updateAbility(admin?.formatted_permissions);
      }

      const isFirstLogin = admin?.password_changed === false;
      const roles = admin?.formatted_roles || [];

      this.postLoginCallbacks.forEach(callback => {
        try {
          callback({ admin, isFirstLogin, roles });
        } catch (err) {
          console.error('Post-login callback failed:', err);
        }
      });

      if (isFirstLogin) {
        navigate('/admin/reset_password');
      } else if (roles.length === 0) {
        navigate('/admin/no_permissions');
      } else {
        navigate(this.isSafeInternalRedirect(redirectTo) ? redirectTo : '/admin/dashboard');
        if (showSuccessToast) {
          showNotification(successMessage, 'success');
        }
      }

      return { isFirstLogin, admin, roles };
    } catch (err) {
      if (err.response?.status === 401 && !this.isLoggingOut) {
        this.logout();
      }
      throw err;
    }
  }

  cleanup() {
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
    this.clearLocalAuth();
  }
}

const authManager = new AuthManager();
export default authManager;
